import LinkedTasksPanel from '../../components/work/v2/LinkedTasksPanel';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Descriptions, Empty, Table, Tag, message } from 'antd';
import { ArrowRightOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { ordersApi } from '../../utils/api';
import { DetailPageSkeleton } from '../../components/SkeletonLoaders';
import { formatMoneyFa as formatMoney } from '../../utils/formatPriceToman';
import { useDetailReturnRefs } from '../../hooks/useListReturnState';
import { useEntityBreadcrumb } from '../../hooks/useEntityBreadcrumb';
import EntityBreadcrumb from '../../components/common/EntityBreadcrumb';

const statusMeta = {
  pending: { text: 'در انتظار', color: 'gold' },
  processing: { text: 'در حال پردازش', color: 'blue' },
  completed: { text: 'تکمیل‌شده', color: 'green' },
  failed: { text: 'ناموفق', color: 'red' },
  'on-hold': { text: 'در حالت انتظار', color: 'cyan' },
  canceled: { text: 'لغوشده', color: 'default' },
};

const ViewOrder = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const orderForCrumb = data?.order;
  const { trail, goToCrumb } = useEntityBreadcrumb({
    listPath: '/orders',
    listLabel: 'سفارش‌ها',
    entityLabel:
      orderForCrumb?.order_number ||
      orderForCrumb?.name ||
      (id ? `سفارش #${id}` : 'سفارش'),
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await ordersApi.getById(id);
        setData(res?.data || null);
      } catch (error) {
        message.error('خطا در دریافت اطلاعات سفارش');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading || !data?.order) {
    return (
      <MainLayout>
        <div style={{ fontFamily: 'CustomFont' }}>
          <DetailPageSkeleton />
        </div>
      </MainLayout>
    );
  }

  const order = data.order;
  const s = statusMeta[order.status] || { text: order.status || '-', color: 'default' };

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <EntityBreadcrumb trail={trail} onNavigate={goToCrumb} />
          <div className="border-b border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800 rounded-t-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center">
                  <ShoppingCartOutlined className="text-emerald-600 dark:text-emerald-400 text-xl" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-0">جزئیات سفارش</h1>
                  <p className="text-gray-500 dark:text-gray-400 text-sm m-0">{order.order_number}</p>
                </div>
              </div>
              <Button
                icon={<ArrowRightOutlined />}
                onClick={() => navigate(returnToRef.current || '/orders', { state: buildGoBackState() })}
              >
                بازگشت
              </Button>
            </div>
          </div>
          <div className="p-6 bg-gray-50 dark:bg-gray-900/50">
            <Card className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 mb-4">
              <Descriptions bordered column={2} style={{ fontFamily: 'CustomFont' }}>
                <Descriptions.Item label="شماره سفارش">{order.order_number}</Descriptions.Item>
                <Descriptions.Item label="وضعیت"><Tag color={s.color}>{s.text}</Tag></Descriptions.Item>
                <Descriptions.Item label="مشتری">{order.client?.name_salutation || order.client?.name || '--'}</Descriptions.Item>
                <Descriptions.Item label="پروژه">{order.project?.project_name || '--'}</Descriptions.Item>
                <Descriptions.Item label="تاریخ سفارش">{order.order_date ? new Date(order.order_date).toLocaleDateString('fa-IR') : '--'}</Descriptions.Item>
                <Descriptions.Item label="مبلغ کل">{formatMoney(order.total, order.currency?.currency_symbol)}</Descriptions.Item>
                <Descriptions.Item label="یادداشت" span={2}>{order.note || '--'}</Descriptions.Item>
              </Descriptions>
            </Card>

            <Card title="آیتم‌ها" className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
              <Table
                rowKey="id"
                dataSource={order.items || []}
                pagination={false}
                locale={{ emptyText: <Empty description="آیتمی وجود ندارد" /> }}
                columns={[
                  { title: 'نام آیتم', dataIndex: 'item_name' },
                  { title: 'تعداد', dataIndex: 'quantity' },
                  { title: 'واحد', dataIndex: ['product', 'unit_id'] },
                  { title: 'قیمت واحد', dataIndex: 'unit_price', render: (v) => formatMoney(v) },
                  { title: 'مبلغ', dataIndex: 'amount', render: (v) => formatMoney(v) },
                ]}
              />
            </Card>

            <Card title="وظایف مرتبط" className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 mt-4">
              <LinkedTasksPanel
                type="order"
                id={id}
                recordLabel={`سفارش ${order.custom_order_number || order.order_number || id}`}
                returnTo={`/orders/${id}`}
              />
            </Card>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default ViewOrder;
